/* eslint-disable prettier/prettier */
/* eslint-disable react/prop-types */
import PolylineIcon from '@mui/icons-material/Polyline'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    Paper,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'
import TemplatePreview from '../../components/TemplatePreview'
import ListPagination from '../../components/shared/ListPagination'
import {
    putSelectedEventType,
    setPipelinePageSize,
    showPipelinePage,
} from '../../features/pipelineSlice'
import { clearTemplateData } from '../../features/templateSlice'
import { roles } from '../../helpers/Constants'
import { clientPage, cursorPage, formatCount } from '../../helpers/pagination'
import { filterPipelinesByName } from '../../helpers/pipelines'
import useAuth from '../../hooks/useAuth'
import useCommsManager from '../../hooks/useCommsManager'
import usePipelineCount from '../../hooks/usePipelineCount'
import useTemplatePreview from '../../hooks/useTemplatePreview'
import PipelineService, {
    PIPELINE_PAGE_SIZES,
} from '../../services/pipelineService'
import TemplateService from '../../services/templateService'
import {
    ACCENT,
    BRAND,
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
    cardSx,
    pageSx,
} from '../../theme/tokens'
import DeletePipelineDialog from './DeletePipelineDialog'
import PayloadDialog from './PayloadDialog'
import { PipelineRow, TABLE_MIN_WIDTH, TableHeaderRow } from './PipelineTable'

export { TABLE_MIN_WIDTH }

// How long typing must pause before the name search asks the service (the
// old app's 500 milliseconds).
export const SEARCH_DEBOUNCE_MS = 500

const StatCard = ({ label, value, color }) => (
    <Paper elevation={0} sx={{ ...cardSx, px: 2, py: 1.5, minWidth: 110 }}>
        <Typography
            variant="h2"
            component="p"
            sx={{
                color: color || TEXT.secondary,
                lineHeight: 1.2,
            }}
        >
            {value}
        </Typography>
        <Typography
            variant="overline"
            component="p"
            sx={{ color: TEXT.faint, lineHeight: 1.6, mt: 0.25 }}
        >
            {label}
        </Typography>
    </Paper>
)

/**
 * What the list shows and what to call it: one event type's active
 * pipelines (loaded whole, the name search filters them here), or a page of
 * the pipelines in a status, narrowed by the name search on the service.
 */
const describeList = ({ status, eventType, search }) => {
    if (eventType)
        return ` for ${eventType}${search ? ` matching "${search}"` : ''}`
    if (search) return ` ${status} matching "${search}"`
    return status === 'inactive' ? ' inactive' : ' total'
}

/** The message under "No pipelines found" for the list being shown. */
const emptyMessage = ({ status, eventType, search }) => {
    if (search)
        return `No ${status} pipeline name or event type contains "${search}".`
    if (eventType)
        return 'This event type has no active pipeline. Clear the filter to see every pipeline.'
    if (status === 'inactive') return 'There are no inactive pipelines.'
    return 'Create a new pipeline to get started.'
}

/**
 * The empty list's heading and message. An empty page that still has a page
 * after it (a search that read several 5,000-row blocks without a match;
 * the list already reads on past empty pages a few times) means "not found
 * yet", never "none": Next keeps reading.
 */
const emptyState = ({ status, eventType, search, morePages }) => {
    if (morePages)
        return {
            title: 'No matches yet in the pipelines read so far',
            message: 'Next reads further.',
        }
    return {
        title: 'No pipelines found',
        message: emptyMessage({ status, eventType, search }),
    }
}

const Pipelines = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { user } = useAuth()
    // Changing pipelines needs the comms manager role, as in the old app.
    const { canManage, note: manageNote } = useCommsManager()
    const {
        pipelines,
        eventTypes,
        selectedEventType,
        listLoading,
        pages,
        cursors,
        pageIndex,
        pageSize,
        pageLoading,
    } = useSelector((state) => state.pipeline)
    const { templates } = useSelector((state) => state.template)
    // No event type means "every pipeline in the status". The page used to
    // pick one on every load (the last one chosen, else the first in the
    // list), so a refresh always came back filtered and clearing the chip
    // left the old rows in place. A choice is kept only while the app stays
    // open (the store is not persisted), so a refresh starts unfiltered.
    const [eventType, setEventType] = useState(selectedEventType || '')
    // `inactive`: pipelines taken out of service but not deleted. The
    // event-type list route returns active pipelines only, so the event type
    // filter applies to Active.
    const [status, setStatus] = useState('active')
    const [query, setQuery] = useState('')
    const [debouncedQuery, setDebouncedQuery] = useState('')
    // The row awaiting delete, and the event type whose payload is open.
    const [pendingDelete, setPendingDelete] = useState(null)
    const [payloadEventType, setPayloadEventType] = useState('')
    const previewTemplate = useTemplatePreview()
    // One event type's list is whole in memory: the page divides it here.
    const [eventPage, setEventPage] = useState(0)
    // Bumped by Refresh and a delete, so the total is counted again.
    const [countKey, setCountKey] = useState(0)
    // How many pipelines the status has: the unfiltered list's total.
    const statusCount = usePipelineCount(status, true, countKey)

    const search = (eventType ? query : debouncedQuery).trim()
    const listArgs = { status, search: eventType ? '' : search, pageSize }
    const byEventType = !!eventType && status === 'active'

    const pendingApprovals = useMemo(
        () => templates.filter((t) => t.status === 'approval_awaited').length,
        [templates]
    )

    // One event type's list is complete, so the name search filters it here
    // and the page shows one page of it; the status list and its search are
    // read a page at a time from the service (features/pipelineSlice).
    const eventRows = useMemo(
        () => (byEventType ? filterPipelinesByName(pipelines, query) : []),
        [byEventType, pipelines, query]
    )
    const view = byEventType
        ? clientPage({ rows: eventRows, page: eventPage, pageSize })
        : cursorPage({
              pages,
              cursors,
              pageIndex,
              // A search's total is known only once it reaches the end.
              knownTotal: listArgs.search ? null : statusCount?.count,
          })
    const rows = view.rows
    useEffect(() => setEventPage(0), [eventType, query, pageSize])

    /** The current list from the start: the event type's pipelines, or the first page. */
    const loadPipelines = () =>
        dispatch(
            byEventType
                ? PipelineService.getPipelines(eventType)
                : PipelineService.listPipelines(listArgs)
        )

    useEffect(() => {
        if (query === debouncedQuery) return undefined
        const timer = setTimeout(
            () => setDebouncedQuery(query),
            SEARCH_DEBOUNCE_MS
        )
        return () => clearTimeout(timer)
    }, [query])

    // Reload when what the list shows changes (not on every keystroke while
    // an event type's complete list is being filtered here). A new page size
    // reads the status list again from its first page.
    const listKey = byEventType
        ? `event:${eventType}`
        : `${status}:${listArgs.search}:${pageSize}`
    useEffect(() => {
        loadPipelines()
        dispatch(putSelectedEventType({ selectedEventType: eventType }))
    }, [listKey])

    useEffect(() => {
        // A preview left open by another page must not pop up here.
        dispatch(clearTemplateData())
        dispatch(PipelineService.getEventTypes())
        // The pending-approvals link counts the templates in the store, which
        // was empty when Pipelines was the first page opened.
        if (!templates?.length)
            dispatch(TemplateService.getTemplates({ background: true }))
    }, [])

    const onStatusChange = (_, value) => {
        if (!value) return
        setStatus(value)
        if (value === 'inactive') setEventType('')
    }

    // The wizard keeps its draft locally, so pass the source row through
    // router state instead of the old Redux draft handoff.
    const onDuplicate = (row) => {
        navigate('/pipelines/new', {
            state: {
                duplicate: {
                    ...row,
                    pipeline_name: `${row.pipeline_name} (copy)`,
                },
            },
        })
    }

    const onRefresh = () => {
        setCountKey((k) => k + 1)
        loadPipelines()
    }

    // The page shown is read again (a later page from its own cursor), so a
    // delete on page 4 stays on page 4.
    const onDeleted = () => {
        setPendingDelete(null)
        setCountKey((k) => k + 1)
        if (!byEventType && pageIndex > 0)
            dispatch(
                PipelineService.listPipelines({
                    ...listArgs,
                    page: pageIndex,
                    cursor: cursors[pageIndex],
                })
            )
        else loadPipelines()
    }

    /** Previous and cached pages show at once; the next new page is read with its cursor. */
    const onPageChange = (next) => {
        if (byEventType) {
            setEventPage(next)
            return
        }
        if (pages[next]) dispatch(showPipelinePage(next))
        else if (next === pages.length && cursors[next])
            dispatch(
                PipelineService.listPipelines({
                    ...listArgs,
                    page: next,
                    cursor: cursors[next],
                })
            )
    }

    // 313 event types on dev, in insertion order: sorted and searchable.
    const sortedEventTypes = useMemo(
        () =>
            [...(eventTypes ?? [])].sort((a, b) =>
                a.localeCompare(b, undefined, { sensitivity: 'base' })
            ),
        [eventTypes]
    )

    if (
        user?.permissions &&
        (user.permissions.includes(roles.DEV_DESIGNER) ||
            user.permissions.includes(roles.PROD_DESIGNER)) &&
        !user.permissions.includes(roles.PROD_ADMIN) &&
        !user.permissions.includes(roles.DEV_ADMIN)
    )
        return <Navigate to="/templates" />

    const readOnly = status === 'inactive'
    const listState = { status, eventType, search }
    const empty = emptyState({
        ...listState,
        morePages: !byEventType && view.hasNext,
    })
    const tableLoading = listLoading || pageLoading
    // "2,211" when the total is known; "100+" while a search reads on.
    const countText =
        view.total !== null
            ? formatCount(view.total)
            : `${formatCount(view.loaded)}+`
    const showPaging =
        !listLoading && (rows.length > 0 || view.hasNext || pageIndex > 0)

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 3, md: 4 } }}>
                {/* Page Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: `${RADIUS.md}px`,
                                background: GRADIENT.brand,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            {/* Deep Blue on the orange-to-yellow tile in
                                both themes (white is 3.3:1 on orange). */}
                            <PolylineIcon
                                sx={{ color: ON_ORANGE, fontSize: ICON.xl }}
                            />
                        </Box>
                        <Box>
                            <Typography
                                variant="h2"
                                component="h1"
                                sx={{ color: TEXT.primary, lineHeight: 1.2 }}
                            >
                                Pipelines
                            </Typography>
                            <Typography
                                variant="caption"
                                component="p"
                                sx={{
                                    color: TEXT.faint,
                                    mt: 0.25,
                                }}
                            >
                                {listLoading
                                    ? `Loading pipelines${
                                          eventType ? ` for ${eventType}` : ''
                                      }…`
                                    : `${countText} pipeline${
                                          view.total === 1 ? '' : 's'
                                      }${describeList(listState)}`}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={onRefresh}
                            disabled={tableLoading}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/bulk-send')}
                        >
                            Bulk Send
                        </Button>
                        <Tooltip title={manageNote}>
                            {/* A disabled button gets no hover; the span does. */}
                            <span>
                                <Button
                                    variant="contained"
                                    disabled={!canManage}
                                    onClick={() => navigate('/pipelines/new')}
                                >
                                    New Pipeline
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Stat Cards */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <StatCard
                        label={
                            search
                                ? 'Matching'
                                : readOnly
                                ? 'Inactive'
                                : 'Active'
                        }
                        value={listLoading ? '…' : countText}
                        color={readOnly ? TEXT.secondary : STATUS.success.fg}
                    />
                </Box>

                {/* Filters Row */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 2.5,
                        flexWrap: 'wrap',
                    }}
                >
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={status}
                        onChange={onStatusChange}
                        aria-label="Pipeline status"
                        sx={{
                            '& .MuiToggleButton-root': {
                                textTransform: 'none',
                                px: 1.75,
                                color: TEXT.secondary,
                                borderColor: SURFACE.border,
                            },
                            '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover':
                                {
                                    background: ACCENT.bg15,
                                    color: TEXT.primary,
                                },
                        }}
                    >
                        <ToggleButton value="active">Active</ToggleButton>
                        <ToggleButton value="inactive">Inactive</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField
                        size="small"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Name or event type"
                        inputProps={{ 'aria-label': 'Search pipelines' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        sx={{
                                            fontSize: ICON.md,
                                            color: TEXT.faint,
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: 260 }}
                    />
                    <Box sx={{ width: 240 }}>
                        <Autocomplete
                            size="small"
                            options={sortedEventTypes}
                            value={eventType || null}
                            onChange={(_, v) => setEventType(v ?? '')}
                            disabled={readOnly}
                            autoHighlight
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Event type"
                                    placeholder="Search event types"
                                />
                            )}
                        />
                    </Box>
                    {eventType && (
                        <Chip
                            label={eventType}
                            onDelete={() => setEventType('')}
                            size="small"
                            sx={{
                                height: 22,
                                typography: 'caption',
                                fontWeight: 500,
                                backgroundColor: ACCENT.bg12,
                                color: ACCENT.text,
                                borderRadius: `${RADIUS.pill}px`,
                                border: `1px solid ${ACCENT.border30}`,
                                '& .MuiChip-deleteIcon': {
                                    fontSize: ICON.sm,
                                    color: ACCENT.text,
                                    '&:hover': { color: TEXT.primary },
                                },
                            }}
                        />
                    )}
                    {pendingApprovals > 0 && (
                        <Box
                            role="link"
                            tabIndex={0}
                            onClick={() => navigate('/templates')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    navigate('/templates')
                                }
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                outline: 'none',
                                typography: 'caption',
                                color: STATUS.warning.fg,
                                '&:hover': { textDecoration: 'underline' },
                                '&:focus-visible': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: STATUS.warning.fg,
                                }}
                            />
                            {pendingApprovals} pending approval
                            {pendingApprovals > 1 ? 's' : ''}
                        </Box>
                    )}
                </Box>
                {readOnly && (
                    <Typography
                        variant="body2"
                        sx={{ color: TEXT.muted, mb: 2 }}
                    >
                        Inactive pipelines are read only: the service does not
                        update an inactive pipeline. Deleted pipelines are not
                        listed here; a row&apos;s Deleted versions shows them.
                    </Typography>
                )}

                {/* Table. Seven text columns need about 950px; narrower (a phone),
                    the table scrolls inside its card instead of squeezing every
                    name to one letter (the grid has no minimum column widths). */}
                <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
                    <Box
                        data-testid="pipelines-table-scroll"
                        sx={{ overflowX: 'auto' }}
                    >
                        <Box sx={{ minWidth: TABLE_MIN_WIDTH }}>
                            <TableHeaderRow />
                            {tableLoading ? (
                                <Box
                                    role="status"
                                    data-testid="pipelines-loading"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1.5,
                                        py: 6,
                                    }}
                                >
                                    <CircularProgress
                                        size={18}
                                        sx={{ color: BRAND.orange }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{ color: TEXT.muted }}
                                    >
                                        Loading pipelines…
                                    </Typography>
                                </Box>
                            ) : rows.length === 0 ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        py: 8,
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        component="p"
                                        sx={{
                                            color: TEXT.faint,
                                            mb: 0.5,
                                        }}
                                    >
                                        {empty.title}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: TEXT.faint }}
                                    >
                                        {empty.message}
                                    </Typography>
                                </Box>
                            ) : (
                                rows.map((pipeline) => (
                                    <PipelineRow
                                        key={pipeline.id}
                                        pipeline={pipeline}
                                        templates={templates}
                                        navigate={navigate}
                                        readOnly={
                                            !canManage ||
                                            readOnly ||
                                            pipeline.status === 'inactive'
                                        }
                                        onDelete={setPendingDelete}
                                        onDuplicate={onDuplicate}
                                        onPreview={previewTemplate}
                                        onViewPayload={setPayloadEventType}
                                    />
                                ))
                            )}
                        </Box>
                    </Box>
                </Paper>
                {showPaging && (
                    <Box sx={{ mt: 2 }}>
                        <ListPagination
                            label="Pipelines pages"
                            page={byEventType ? view.page : pageIndex}
                            pageSize={pageSize}
                            pageSizeOptions={PIPELINE_PAGE_SIZES}
                            onPageChange={onPageChange}
                            onPageSizeChange={(size) =>
                                dispatch(setPipelinePageSize(size))
                            }
                            from={view.from}
                            to={view.to}
                            total={view.total}
                            hasNext={view.hasNext}
                            // Page numbers only where any page can be opened
                            // directly: cursor pages are read in order.
                            pageCount={byEventType ? view.pageCount : undefined}
                            disabled={pageLoading}
                        />
                    </Box>
                )}
            </Box>
            <DeletePipelineDialog
                pipeline={pendingDelete}
                onClose={() => setPendingDelete(null)}
                onDeleted={onDeleted}
            />
            <PayloadDialog
                eventType={payloadEventType}
                onClose={() => setPayloadEventType('')}
            />
            <TemplatePreview />
        </Box>
    )
}

export default Pipelines
